import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCheck, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { api, unwrap, unwrapPage } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import type { components, operations } from '@/api/schema';
import { PermissionGate } from '@/auth/PermissionGate';
import { useStepUp } from '@/auth/StepUp';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { ConfirmarAccion } from '@/components/ConfirmarAccion';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Paginador } from '@/components/Paginador';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';
import { fechaHora } from '@/lib/formato';

type Intento = components['schemas']['ExamAttempt'];
type Filtros = NonNullable<operations['getAdminExamAttempts']['parameters']['query']>;

const CLAVES = ['q', 'kind', 'status', 'userId'] as const;
const ESTADOS: Record<string, string> = {
  in_progress: 'En curso',
  paused: 'En pausa',
  finished: 'Terminado',
};

function minutos(s: number) {
  return s < 60 ? `${s} s` : `${Math.round(s / 60)} min`;
}

function Resueltos() {
  const editable = useCan('preguntas', 'editar');
  const qc = useQueryClient();
  const { filtros, cambiar, query } = useFiltrosUrl(CLAVES);
  const { proteger, dialogo } = useStepUp();
  const [cerrar, setCerrar] = useState<Intento | null>(null);
  const [borrar, setBorrar] = useState<Intento | null>(null);
  const q = useQuery({
    queryKey: ['preguntas', 'resueltos', query, filtros.page],
    queryFn: () =>
      unwrapPage<Intento[]>(
        api.GET('/api/v1/admin/exam-attempts', {
          params: { query: { ...(query as Filtros), page: filtros.page, pageSize: 50 } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  const refrescar = () => void qc.invalidateQueries({ queryKey: ['preguntas', 'resueltos'] });
  const terminar = useMutation({
    mutationFn: (id: number) =>
      unwrap(
        api.POST('/api/v1/admin/exam-attempts/{id}/finish', {
          params: { path: { id: String(id) } },
        }),
      ),
    onSuccess: () => {
      toast.success('Intento terminado y calificado.');
      setCerrar(null);
      refrescar();
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  const quitar = useMutation({
    mutationFn: (id: number) =>
      proteger(() =>
        unwrap(
          api.DELETE('/api/v1/admin/exam-attempts/{id}', { params: { path: { id: String(id) } } }),
        ),
      ),
    onSuccess: () => {
      toast.success('Intento borrado: el alumno puede volver a presentarlo.');
      setBorrar(null);
      refrescar();
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  const sel = (k: (typeof CLAVES)[number], etiqueta: string, op: [string, string][]) => (
    <Campo etiqueta={etiqueta}>
      <select
        className="input-vidrio"
        value={filtros[k]}
        onChange={(e) => cambiar({ [k]: e.target.value })}
      >
        {op.map(([v, t]) => (
          <option key={v} value={v}>
            {t}
          </option>
        ))}
      </select>
    </Campo>
  );
  return (
    <>
      <Encabezado
        titulo="Exámenes resueltos"
        descripcion="Exámenes y simuladores de los alumnos en la plataforma nueva. Un intento que quedó abierto se puede terminar (se califica con lo que respondió) o borrar para que lo repita."
      />
      <div className="panel-vidrio mb-4 grid gap-3 sm:grid-cols-3">
        <Campo etiqueta="Buscar">
          <input
            className="input-vidrio"
            placeholder="Alumno o examen"
            defaultValue={filtros.q}
            onKeyDown={(e) => e.key === 'Enter' && cambiar({ q: e.currentTarget.value })}
            onBlur={(e) => e.target.value !== filtros.q && cambiar({ q: e.target.value })}
          />
        </Campo>
        {sel('kind', 'Tipo', [
          ['', 'Todos'],
          ['exam', 'Exámenes'],
          ['simulator', 'Simuladores'],
        ])}
        {sel('status', 'Estado', [
          ['', 'Todos'],
          ['in_progress', 'En curso'],
          ['paused', 'En pausa'],
          ['finished', 'Terminados'],
        ])}
      </div>
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data?.data.length === 0 && <Vacio titulo="Sin intentos con esos filtros" />}
      {q.data && q.data.data.length > 0 && (
        <div className="panel-vidrio overflow-x-auto p-0">
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                <th>Alumno</th>
                <th>Examen</th>
                <th>Estado</th>
                <th className="text-right">Respondidas</th>
                <th className="text-right">Aciertos</th>
                <th>Tiempo</th>
                <th>Última actividad</th>
                {editable && <th />}
              </tr>
            </thead>
            <tbody>
              {q.data.data.map((a) => (
                <tr key={a.id}>
                  <td>
                    <Link to={`/alumnos/${a.userId}`} className="font-semibold hover:underline">
                      {a.userName}
                    </Link>
                    <span className="block text-xs text-texto-3">{a.userEmail}</span>
                  </td>
                  <td>
                    {a.name}
                    <span className="block text-xs text-texto-3">
                      {a.kind === 'exam' ? 'Examen' : 'Simulador'} · {a.mode}
                    </span>
                  </td>
                  <td>
                    <span
                      className={a.status === 'finished' ? 'insignia insignia-activa' : 'insignia'}
                    >
                      {ESTADOS[a.status] ?? a.status}
                    </span>
                  </td>
                  <td className="tabular text-right">
                    {a.answeredCount} / {a.questionCount}
                  </td>
                  <td className="tabular text-right">
                    {a.correctCount}
                    {a.status === 'finished' && a.questionCount > 0 && (
                      <span className="text-xs text-texto-3">
                        {' '}
                        ({Math.round((a.correctCount * 100) / a.questionCount)}%)
                      </span>
                    )}
                  </td>
                  <td>{minutos(a.durationSeconds)}</td>
                  <td className="whitespace-nowrap">{a.updatedAt && fechaHora(a.updatedAt)}</td>
                  {editable && (
                    <td className="whitespace-nowrap text-right">
                      {a.status !== 'finished' && (
                        <button
                          type="button"
                          className="btn-cs btn-vidrio px-2 py-1"
                          title="Terminar y calificar"
                          aria-label="Terminar y calificar"
                          onClick={() => setCerrar(a)}
                        >
                          <CheckCheck size={14} aria-hidden />
                        </button>
                      )}{' '}
                      <button
                        type="button"
                        className="btn-cs btn-vidrio px-2 py-1 text-rojo-texto"
                        title="Borrar para que lo repita"
                        aria-label="Borrar intento"
                        onClick={() => setBorrar(a)}
                      >
                        <Trash2 size={14} aria-hidden />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {q.data && (
        <Paginador
          page={q.data.page}
          pageSize={q.data.pageSize}
          total={q.data.total}
          onPage={(page) => cambiar({ page })}
        />
      )}
      <ConfirmarAccion
        abierto={cerrar !== null}
        onCerrar={() => setCerrar(null)}
        titulo="Terminar el intento"
        descripcion={`Se califica con lo que ${cerrar?.userName ?? 'el alumno'} respondió (${cerrar?.answeredCount ?? 0} de ${cerrar?.questionCount ?? 0}); lo que no respondió cuenta como incorrecto.`}
        textoBoton="Terminar y calificar"
        ocupado={terminar.isPending}
        onConfirmar={() => cerrar && terminar.mutate(cerrar.id)}
      />
      <ConfirmarAccion
        abierto={borrar !== null}
        onCerrar={() => setBorrar(null)}
        titulo="Borrar el intento"
        descripcion="Se borran sus respuestas y su resultado; el alumno podrá presentarlo de nuevo. No se puede deshacer."
        textoBoton="Borrar"
        peligro
        ocupado={quitar.isPending}
        onConfirmar={() => borrar && quitar.mutate(borrar.id)}
      />
      {dialogo}
    </>
  );
}

export function ResueltosPage() {
  return (
    <PermissionGate seccion="preguntas">
      <Resueltos />
    </PermissionGate>
  );
}
